/**
 * BookSearchResults — dedicated infinite-scroll results view, shown on
 * Explore in place of the normal dashboard sections while a search is
 * active. Real Open Library pagination (not a client-side reveal): each
 * scroll-to-bottom calls fetchNextPage(), which requests the next page
 * from Open Library.
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import { MascotContainer } from '@/components/site/primitives';
import { fadeUp, stagger } from '@/lib/motion';
import { useBookSearch } from '@/hooks/useBookSearch';
import { bookHref } from '@/lib/bookModel';
import BookCard from './BookCard';
import beeBack from '@/assets/bee-back.png';

function BookCardSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="aspect-[2/3] rounded-r-md rounded-l-[4px] bg-line/60" />
      <div className="mt-2.5 space-y-1.5">
        <div className="h-3 w-4/5 rounded bg-line/60" />
        <div className="h-2.5 w-1/2 rounded bg-line/50" />
      </div>
    </div>
  );
}

const GRID_CLASS = 'grid grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 md:gap-x-6 md:gap-y-9';

export default function BookSearchResults({ query, onBack }) {
  const { books, numFound, isLoading, isFetchingNextPage, isError, hasNextPage, fetchNextPage, refetch } = useBookSearch(query);
  const navigate = useNavigate();
  const sentinelRef = useRef(null);

  const openBookDetails = (book) => navigate(bookHref(book), { state: { hint: book } });

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
      },
      { rootMargin: '400px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="u-container mt-8" data-testid="book-search-results">
      <button type="button" onClick={onBack} className="flex items-center gap-1.5 text-sm font-medium text-subtle hover:text-coral">
        <ArrowLeft className="h-4 w-4" /> Back to Explore
      </button>

      <div className="mt-3 flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="font-display text-2xl font-semibold text-ink sm:text-3xl">
          Results for &ldquo;{query}&rdquo;
        </h1>
        {numFound > 0 && <p className="text-sm text-subtle">{numFound.toLocaleString()} books found</p>}
      </div>

      {isLoading ? (
        <div className={`mt-8 ${GRID_CLASS}`}>
          {[...Array(12)].map((_, i) => <BookCardSkeleton key={i} />)}
        </div>
      ) : isError ? (
        <div className="mt-16 flex flex-col items-center gap-4 text-center">
          <p className="text-sm text-subtle">We couldn&rsquo;t load books right now. Please try again.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 rounded-full border border-line bg-card px-4 py-2 text-sm font-semibold text-ink hover:border-coral/40 hover:text-coral"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Retry
          </button>
        </div>
      ) : books.length === 0 ? (
        <div className="mt-10 flex min-h-[280px] items-center justify-center">
          <motion.div variants={fadeUp} initial="hidden" animate="show" className="mx-auto max-w-sm text-center">
            <MascotContainer src={beeBack} alt="" className="mx-auto h-24 w-24" />
            <h2 className="mt-4 font-display text-lg font-semibold text-ink">No books found.</h2>
            <p className="mt-2 text-sm leading-relaxed text-subtle">Try another title, author, or ISBN.</p>
          </motion.div>
        </div>
      ) : (
        <>
          <motion.div variants={stagger} initial="hidden" animate="show" className={`mt-8 ${GRID_CLASS}`}>
            {books.map((book, i) => (
              <motion.div key={book.id} variants={fadeUp} custom={i % 12}>
                <BookCard book={book} onOpenDetails={openBookDetails} />
              </motion.div>
            ))}
          </motion.div>

          {isFetchingNextPage && (
            <div className={`mt-6 ${GRID_CLASS}`}>
              {[...Array(6)].map((_, i) => <BookCardSkeleton key={i} />)}
            </div>
          )}

          {hasNextPage && <div ref={sentinelRef} className="h-10 w-full" aria-hidden="true" />}
        </>
      )}

      <p className="mt-10 pb-4 text-center text-[11px] text-subtle">Book data provided by Open Library</p>
    </div>
  );
}
